import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PolandMap } from './PolandMap';
import { TrendingUp, Image, Users, MessageCircle, ThumbsUp, Loader2, Upload, Gift, ShoppingCart } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const RightSidebar = () => {
    const { isAuthenticated } = useAuth();
    const navigate = useNavigate();
    const [groups, setGroups] = useState([]);
    const [photos, setPhotos] = useState([]);
    const [gadgets, setGadgets] = useState([]);
    const [currentGadgetIndex, setCurrentGadgetIndex] = useState(0);
    const [loading, setLoading] = useState(true);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadFile, setUploadFile] = useState(null);
    const [uploadTitle, setUploadTitle] = useState('');
    const fileInputRef = useRef(null);

    useEffect(() => {
        fetchData();
    }, []);

    // Rotate gadgets every 5 seconds
    useEffect(() => {
        if (gadgets.length > 1) {
            const interval = setInterval(() => {
                setCurrentGadgetIndex(prev => (prev + 1) % gadgets.length);
            }, 5000);
            return () => clearInterval(interval);
        }
    }, [gadgets]);

    const fetchData = async () => {
        try {
            // Fetch popular groups
            const groupsRes = await fetch(`${API_URL}/api/homepage/popular-groups?limit=5`);
            if (groupsRes.ok) {
                const data = await groupsRes.json();
                setGroups(data.groups || []);
            }

            // Fetch recent photos
            const photosRes = await fetch(`${API_URL}/api/homepage/recent-photos?limit=6`);
            if (photosRes.ok) {
                const data = await photosRes.json();
                setPhotos(data.photos || []);
            }

            // Fetch featured gadgets
            const gadgetsRes = await fetch(`${API_URL}/api/homepage/featured-gadgets?count=5`);
            if (gadgetsRes.ok) {
                const data = await gadgetsRes.json();
                setGadgets(data.gadgets || []);
            }
        } catch (error) {
            console.error('Error fetching right sidebar data:', error);
        }
        setLoading(false);
    };

    const handleUploadClick = () => {
        if (!isAuthenticated) {
            toast.error('Musisz być zalogowany, żeby dodać zdjęcie');
            return;
        }
        setShowUploadModal(true);
    };

    const handleFileSelect = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            if (!file.type.startsWith('image/')) {
                toast.error('Wybierz plik graficzny');
                return;
            }
            if (file.size > 10 * 1024 * 1024) {
                toast.error('Plik jest za duży (max 10MB)');
                return;
            }
            setUploadFile(file);
        }
    };

    const handleUploadSubmit = async () => {
        if (!uploadFile) {
            toast.error('Wybierz zdjęcie');
            return;
        }

        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', uploadFile);
            formData.append('title', uploadTitle || 'Zdjęcie klasowe');
            formData.append('category', 'klasowe');
            formData.append('album', 'Zdjęcia klasowe');

            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/api/photos/upload`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                body: formData
            });

            if (response.ok) {
                toast.success('Zdjęcie zostało dodane!');
                setShowUploadModal(false);
                setUploadFile(null);
                setUploadTitle('');
                fetchData(); // Refresh photos
            } else {
                const error = await response.json();
                toast.error(error.detail || 'Błąd podczas dodawania zdjęcia');
            }
        } catch (error) {
            toast.error('Błąd połączenia');
        }
        setUploading(false);
    };

    const currentGadget = gadgets[currentGadgetIndex];

    return (
        <aside className="space-y-4">
            {/* Interactive Poland Map */}
            <PolandMap />

            {/* Trending Groups */}
            <div className="nk-box overflow-hidden">
                <div className="nk-box-header flex items-center gap-2">
                    <TrendingUp className="w-4 h-4" />
                    Popularne grupy
                </div>
                <div className="p-3 space-y-2">
                    {loading ? (
                        <div className="flex justify-center py-4">
                            <Loader2 className="w-5 h-5 animate-spin text-primary" />
                        </div>
                    ) : groups.length > 0 ? (
                        <>
                            {groups.map((group, index) => (
                                <Link 
                                    key={group.id}
                                    to={`/groups/${group.slug || group.id}`}
                                    className="block p-2 rounded-lg hover:bg-muted transition-colors cursor-pointer group animate-fade-in-up"
                                    style={{ animationDelay: `${index * 50}ms` }}
                                >
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="flex-1 min-w-0">
                                            <h4 className="text-xs font-medium text-foreground line-clamp-2 group-hover:text-primary transition-colors">
                                                {group.name}
                                            </h4>
                                            <div className="flex items-center gap-2 mt-1 text-[10px] text-muted-foreground">
                                                <span className="flex items-center gap-0.5">
                                                    <Users className="w-3 h-3" />
                                                    {group.members}
                                                </span>
                                                <span className="flex items-center gap-0.5">
                                                    <MessageCircle className="w-3 h-3" />
                                                    {group.newPosts} postów
                                                </span>
                                            </div>
                                        </div>
                                        {group.newPosts > 5 && (
                                            <Badge className="nk-badge text-[9px] px-1.5 flex-shrink-0">
                                                HOT
                                            </Badge>
                                        )}
                                    </div>
                                </Link>
                            ))}
                            <Link to="/groups">
                                <Button variant="ghost" className="w-full text-xs text-primary hover:text-primary-light h-8">
                                    Zobacz wszystkie grupy
                                </Button>
                            </Link>
                        </>
                    ) : (
                        <div className="text-center py-2">
                            <p className="text-xs text-muted-foreground mb-2">Brak grup</p>
                            <Link to="/groups">
                                <Button size="sm" className="nk-button text-xs">
                                    Utwórz grupę
                                </Button>
                            </Link>
                        </div>
                    )}
                </div>
            </div>

            {/* New Class Photos */}
            <div className="nk-box overflow-hidden">
                <div className="nk-box-header flex items-center gap-2">
                    <Image className="w-4 h-4" />
                    Nowe zdjęcia klasowe
                </div>
                <div className="p-3">
                    {loading ? (
                        <div className="flex justify-center py-4">
                            <Loader2 className="w-5 h-5 animate-spin text-primary" />
                        </div>
                    ) : photos.length > 0 ? (
                        <>
                            <div className="grid grid-cols-2 gap-2">
                                {photos.map((photo, index) => (
                                    <div 
                                        key={photo.id} 
                                        className="group relative cursor-pointer animate-scale-in"
                                        style={{ animationDelay: `${index * 100}ms` }}
                                    >
                                        <div className="nk-avatar aspect-square overflow-hidden">
                                            <img 
                                                src={photo.url.startsWith('http') ? photo.url : `${API_URL}${photo.url}`} 
                                                alt={photo.album}
                                                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                                                onError={(e) => { e.target.src = 'https://via.placeholder.com/150?text=Zdjęcie'; }}
                                            />
                                        </div>
                                        <div className="absolute inset-0 bg-foreground/60 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex flex-col items-center justify-center">
                                            <span className="text-primary-foreground text-[10px] font-medium text-center px-1">
                                                {photo.album}
                                            </span>
                                            <span className="flex items-center gap-1 text-primary-foreground text-[9px] mt-1">
                                                <ThumbsUp className="w-3 h-3" />
                                                {photo.likes}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <Link to="/gallery">
                                <Button variant="ghost" className="w-full text-xs text-primary hover:text-primary-light mt-3 h-8">
                                    Zobacz więcej zdjęć
                                </Button>
                            </Link>
                        </>
                    ) : (
                        <div className="text-center py-4">
                            <p className="text-xs text-muted-foreground mb-2">Brak zdjęć</p>
                            <Button size="sm" className="nk-button-green text-xs" onClick={handleUploadClick}>
                                Dodaj pierwsze zdjęcie
                            </Button>
                        </div>
                    )}
                </div>
            </div>

            {/* Nostalgic Ad Banner - Add Photo CTA */}
            <div className="nk-box overflow-hidden bg-gradient-to-br from-accent-yellow/20 to-accent-green/20">
                <div className="p-4 text-center">
                    <p className="font-display text-sm font-bold text-foreground mb-1">
                        Pamiętasz swoją klasę?
                    </p>
                    <p className="text-xs text-muted-foreground mb-3">
                        Dodaj zdjęcie klasowe i oznacz znajomych!
                    </p>
                    <Button className="nk-button-green text-xs" onClick={handleUploadClick}>
                        <Image className="w-3.5 h-3.5 mr-1" />
                        Dodaj zdjęcie
                    </Button>
                </div>
            </div>

            {/* Rotating Gadgets - Shop promotion */}
            {currentGadget && (
                <div className="nk-box overflow-hidden">
                    <div className="p-3 text-center">
                        <div className="text-4xl mb-2 animate-bounce">{currentGadget.icon}</div>
                        <p className="text-xs font-semibold text-foreground">
                            {currentGadget.name}
                        </p>
                        <p className="text-[10px] text-muted-foreground mt-1">
                            {currentGadget.description?.substring(0, 50)}...
                        </p>
                        <div className="flex items-center justify-center gap-2 mt-2">
                            <Badge className="bg-amber-500 text-white text-[10px]">
                                {currentGadget.price} Zorki
                            </Badge>
                            <Link to="/zorki">
                                <Button size="sm" className="h-6 text-[10px] px-2">
                                    <ShoppingCart className="w-3 h-3 mr-1" />
                                    Kup
                                </Button>
                            </Link>
                        </div>
                        {/* Dots indicator */}
                        <div className="flex justify-center gap-1 mt-2">
                            {gadgets.map((_, idx) => (
                                <span 
                                    key={idx}
                                    className={`w-1.5 h-1.5 rounded-full transition-colors ${
                                        idx === currentGadgetIndex ? 'bg-primary' : 'bg-muted-foreground/30'
                                    }`}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Upload Photo Modal */}
            <Dialog open={showUploadModal} onOpenChange={setShowUploadModal}>
                <DialogContent className="nk-box p-0 max-w-md">
                    <DialogHeader className="nk-box-header">
                        <DialogTitle className="flex items-center gap-2">
                            <Upload className="w-5 h-5" />
                            Dodaj zdjęcie klasowe
                        </DialogTitle>
                    </DialogHeader>
                    <div className="p-4 space-y-4">
                        <div>
                            <Label>Tytuł/Opis zdjęcia</Label>
                            <Input
                                value={uploadTitle}
                                onChange={(e) => setUploadTitle(e.target.value)}
                                placeholder="np. Klasa 3B, rok 2005"
                                className="mt-1"
                            />
                        </div>
                        
                        <div>
                            <Label>Zdjęcie</Label>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                onChange={handleFileSelect}
                                className="hidden"
                            />
                            
                            {uploadFile ? (
                                <div className="mt-2 relative">
                                    <img
                                        src={URL.createObjectURL(uploadFile)}
                                        alt="Preview"
                                        className="w-full h-48 object-cover rounded-lg"
                                    />
                                    <Button
                                        size="sm"
                                        variant="destructive"
                                        className="absolute top-2 right-2"
                                        onClick={() => setUploadFile(null)}
                                    >
                                        Usuń
                                    </Button>
                                </div>
                            ) : (
                                <div 
                                    onClick={() => fileInputRef.current?.click()}
                                    className="mt-2 border-2 border-dashed border-muted-foreground/30 rounded-lg p-8 text-center cursor-pointer hover:border-primary transition-colors"
                                >
                                    <Upload className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                                    <p className="text-sm text-muted-foreground">
                                        Kliknij aby wybrać zdjęcie
                                    </p>
                                    <p className="text-xs text-muted-foreground mt-1">
                                        JPG, PNG do 10MB
                                    </p>
                                </div>
                            )}
                        </div>

                        <div className="flex gap-2">
                            <Button 
                                variant="outline" 
                                className="flex-1"
                                onClick={() => setShowUploadModal(false)}
                            >
                                Anuluj
                            </Button>
                            <Button 
                                className="flex-1 nk-button"
                                onClick={handleUploadSubmit}
                                disabled={uploading || !uploadFile}
                            >
                                {uploading ? (
                                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Wysyłanie...</>
                                ) : (
                                    <><Upload className="w-4 h-4 mr-2" />Dodaj zdjęcie</>
                                )}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </aside>
    );
};
